/**
 * Persisted cart-sync bookkeeping.
 *
 * Deliberately THREE localStorage keys rather than one blob:
 *
 *   - the device id must survive a state wipe, or a shared terminal mints a new
 *     one per login, fills the server's 10-slot device map with dead ids, and
 *     the browser then reads its own former identity as "another device active"
 *     and polls fast forever;
 *   - the guard flags (clear evidence, apply-in-progress) are the two things
 *     that prevent cart destruction, so a lost update on the main blob must not
 *     be able to erase them.
 *
 * Every read is defensive: localStorage can throw (Safari private mode, storage
 * disabled), and a corrupt value must degrade to "no state", never to a crash
 * that leaves sync half-initialized.
 */

import type { ApplyInProgress, ClearEvidence, SyncGuards, SyncState } from "./types.js";

const DEVICE_KEY = "wp_cart_sync_device";
const GUARDS_KEY = "wp_cart_sync_guards";
const STATE_KEY = "wp_cart_sync";

/** Server-accepted device id shape; a mismatch is silently ignored on GET. */
const DEVICE_ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable or full. Sync degrades to per-page behavior, which is
    // safe — it just re-derives state on the next load.
  }
}

function removeKey(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function randomDeviceId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  // Fallback still satisfies the server's [A-Za-z0-9-]{8,64}.
  let id = "";
  for (let i = 0; i < 4; i += 1) {
    id += Math.random().toString(36).slice(2, 10);
  }
  return id.slice(0, 32);
}

/** Stable per-browser id. Survives every state wipe by design. */
export function getDeviceId(): string {
  const stored = readJson<{ deviceId?: string }>(DEVICE_KEY);
  if (stored?.deviceId && DEVICE_ID_PATTERN.test(stored.deviceId)) {
    return stored.deviceId;
  }
  const deviceId = randomDeviceId();
  writeJson(DEVICE_KEY, { deviceId });
  return deviceId;
}

export function loadState(): SyncState | null {
  const state = readJson<Partial<SyncState>>(STATE_KEY);
  if (
    !state ||
    typeof state.customerKey !== "string" ||
    typeof state.locationId !== "number" ||
    typeof state.lastAppliedVersion !== "number" ||
    typeof state.lastAppliedHash !== "string"
  ) {
    return null;
  }
  return {
    customerKey: state.customerKey,
    locationId: state.locationId,
    lastAppliedVersion: state.lastAppliedVersion,
    lastAppliedHash: state.lastAppliedHash,
    lastPushedCartToken: state.lastPushedCartToken ?? null,
    unavailableLines: Array.isArray(state.unavailableLines)
      ? state.unavailableLines.filter((key): key is string => typeof key === "string")
      : [],
  };
}

export function saveState(state: SyncState): void {
  writeJson(STATE_KEY, state);
}

export function clearState(): void {
  removeKey(STATE_KEY);
}

export function loadGuards(): SyncGuards {
  const guards = readJson<Partial<SyncGuards>>(GUARDS_KEY);
  return {
    clearEvidence: guards?.clearEvidence ?? null,
    applyInProgress: guards?.applyInProgress ?? null,
    emptyLocalSince:
      typeof guards?.emptyLocalSince === "number" ? guards.emptyLocalSince : null,
  };
}

function saveGuards(guards: SyncGuards): void {
  if (!guards.clearEvidence && !guards.applyInProgress && guards.emptyLocalSince === null) {
    removeKey(GUARDS_KEY);
    return;
  }
  writeJson(GUARDS_KEY, guards);
}

export function setClearEvidence(evidence: ClearEvidence | null): void {
  saveGuards({ ...loadGuards(), clearEvidence: evidence });
}

export function setApplyInProgress(apply: ApplyInProgress | null): void {
  saveGuards({ ...loadGuards(), applyInProgress: apply });
}

/**
 * Stamp (or clear) the moment this device started seeing an unexplained empty
 * cart. Persisted rather than held in memory because the buyer's return from
 * checkout is a fresh page load, which is exactly when the timer must survive.
 */
export function setEmptyLocalSince(at: number | null): void {
  saveGuards({ ...loadGuards(), emptyLocalSince: at });
}
